import { useEffect, useRef } from "react";

export type PersistedSize = { width: string; height: string };

function getStoredSize(storageKey: string, defaultSize: PersistedSize): PersistedSize {
	try {
		const stored = localStorage.getItem(storageKey);
		if (stored) {
			const parsed = JSON.parse(stored) as PersistedSize;
			const w = parseInt(parsed.width);
			const h = parseInt(parsed.height);
			if (w > 100 && h > 100) return parsed;
		}
	} catch {}
	return defaultSize;
}

/**
 * Persists the size of an element to localStorage.
 * Returns a ref to attach to the element and the last stored size.
 *
 * @param storageKey  - localStorage key to read/write
 * @param open        - whether the element is currently visible (observer is only active when true)
 * @param defaultSize - fallback size when nothing is stored yet
 */
export function usePersistedSize(
	storageKey: string,
	open: boolean,
	defaultSize: PersistedSize = { width: "80vw", height: "80vh" },
): { ref: React.RefObject<HTMLDivElement>; size: PersistedSize } {
	const ref = useRef<HTMLDivElement>(null);
	const size = getStoredSize(storageKey, defaultSize);

	useEffect(() => {
		if (!open) return;
		let observer: ResizeObserver | null = null;
		const frameId = requestAnimationFrame(() => {
			const el = ref.current;
			if (!el) return;
			observer = new ResizeObserver(() => {
				// Use offsetWidth/offsetHeight to capture the full border-box size.
				// (resize:both resizes the border box, not the content box.)
				const width = el.offsetWidth;
				const height = el.offsetHeight;
				if (width > 100 && height > 100) {
					localStorage.setItem(storageKey, JSON.stringify({ width: `${width}px`, height: `${height}px` }));
				}
			});
			observer.observe(el);
		});
		return () => {
			cancelAnimationFrame(frameId);
			observer?.disconnect();
		};
	}, [open, storageKey]);

	return { ref, size };
}
